import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import moment from 'moment';

import {
  addContractor,
  archiveContractor,
  cleanPayout,
  getPayoutGrid,
  renameContractor,
  setContractorCell,
} from '@/store/actions/payout';
import { selectPayoutGrid } from '@/store/selectors/payout';
import { PayoutCell, PayoutGrid, PayoutGridRow } from '@/store/reducers/payout';

import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import Navigation from '@/components/Navigation';
import Loader from '@/components/common/Loader';
import ContractorCell from '@/components/payout/ContractorCell';
import ContractorSheet from '@/components/payout/ContractorSheet';
import PayeeSheet from '@/components/payout/PayeeSheet';
import { formatMoney } from '@/utils/money';

interface Props extends RouteComponentProps {}

// Spelled out rather than "Sep 26", which reads as a day of the month. A period
// is always a whole calendar month.
const columnLabel = (period: string) => moment(period, 'YYYY-MM').format('MMM YYYY');

// Hryvnia expenses are owed on top of the dollar figure, never converted into
// it. A 96px column has no room for both on one line, so the hryvnia sits
// underneath.
const amountText = (usd: number | undefined, uah: number | undefined) => {
  if (usd === undefined && !uah) return '—';
  if (!uah) return formatMoney(usd!);
  if (usd === undefined) return formatMoney(uah, 'UAH');

  return (
    <>
      {formatMoney(usd)}
      <Uah>{`+ ${formatMoney(uah, 'UAH')}`}</Uah>
    </>
  );
};

const cellText = (cell: PayoutCell) => amountText(cell.amount, cell.uah);

const totalText = (grid: PayoutGrid, period: string) => amountText(
  grid.totals[period],
  grid.totalsUah ? grid.totalsUah[period] : undefined,
);

/**
 * The payout table the accounting spreadsheet used to be.
 *
 * Two layouts, because two columns and forty-four want different things: the
 * default previous-and-next view fits the shell with room to spare and is laid
 * out as full-width rows, while the full history needs the sticky name column
 * and a horizontal scroller.
 *
 * Recorded and projected cells are told apart by colour. A recorded cell is what
 * was actually paid and never moves; a projected one is today's salary plus
 * expenses and shifts whenever either is edited.
 */
const Payouts: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const grid = useSelector(selectPayoutGrid);
  const [{ payout }] = useLanguage();
  const scroller = useRef<HTMLDivElement>(null);

  const [showAll, toggleShowAll] = useToggle(false);
  const [showArchived, toggleShowArchived] = useToggle(false);
  const [showUnpaid, toggleShowUnpaid] = useToggle(false);
  const [showContractors, toggleShowContractors] = useToggle(true);
  const [isAdding, toggleIsAdding] = useToggle(false);
  const [newName, setNewName] = useState('');
  const [openRow, setOpenRow] = useState<PayoutGridRow | null>(null);

  // A layout effect, so the stored grid renders before the first paint -- see
  // store/responses.ts. Each of the two views is stored separately.
  useLayoutEffect(() => {
    dispatch(getPayoutGrid(showAll));
  }, [showAll]);

  useEffect(() => () => {
    dispatch(cleanPayout());
  }, []);

  // Columns run oldest to newest like the sheet did, but the months anyone
  // actually wants are the last few, so open at the right-hand edge.
  //
  // Done twice on purpose: the web fonts land after first paint and widen every
  // column, so a single pass scrolls to a stale width and stops short.
  useEffect(() => {
    if (!grid || !grid.showAll) return undefined;

    let cancelled = false;
    const toEnd = () => {
      if (!cancelled && scroller.current) scroller.current.scrollLeft = 1e9;
    };

    const frame = requestAnimationFrame(toEnd);
    const { fonts } = document as Document & { fonts?: { ready: Promise<unknown> } };
    if (fonts) fonts.ready.then(toEnd).catch(() => undefined);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [grid]);

  if (!grid) {
    return (
      <>
        <Loader />
        <Navigation path="payouts" />
      </>
    );
  }

  // Independent switches: archived is a former payee, unpaid was never one, and
  // contractors are a different kind of payee rather than a state. Contractors
  // are on by default -- they are part of what gets paid out.
  const rows = grid.rows.filter((row) => (
    (showArchived || !row.isArchived)
    && (showUnpaid || !row.isUnpaid)
    && (showContractors || !row.isContractor)
  ));

  // The accounting sheet split the table into EMPLOYEES and CONTRACTORS, and the
  // distinction still matters, so the rows carry a heading at the boundary
  // rather than a badge each. Rows arrive employees-first from the server.
  const firstContractorRef = (rows.find((row) => row.isContractor) || {}).ref;

  const handleAdd = () => {
    if (!newName.trim()) return;
    dispatch(addContractor(newName.trim(), showAll));
    setNewName('');
    toggleIsAdding(false);
  };

  // Opens over the table rather than navigating, so reviewing a column does not
  // cost you your place in it. Both kinds of payee get a sheet, but a different
  // one: a contractor has no salary or expenses, only a name and whether they
  // are still being paid.
  const openPayee = (row: PayoutGridRow) => setOpenRow(row);

  const renderAmount = (row: PayoutGridRow, cell: PayoutCell) => (
    row.isContractor ? (
      <ContractorCell
        amount={cell.amount}
        onChange={(amount) => dispatch(
          setContractorCell(row.contractorId!, cell.period, amount, showAll),
        )}
      />
    ) : cellText(cell)
  );

  return (
    <>
      <Header>
        <HeaderText>{payout.payouts}</HeaderText>
        <HeaderNote>
          {`${rows.length} ${payout.people} · ${grid.periods.length} ${payout.months}`}
        </HeaderNote>
      </Header>

      <Controls>
        <Toggle active={showAll} onClick={() => toggleShowAll(!showAll)}>
          {payout.all_months}
        </Toggle>
        <Toggle active={showArchived} onClick={() => toggleShowArchived(!showArchived)}>
          {payout.show_archived}
        </Toggle>
        <Toggle active={showUnpaid} onClick={() => toggleShowUnpaid(!showUnpaid)}>
          {payout.show_unpaid}
        </Toggle>
        <Toggle
          active={showContractors}
          onClick={() => toggleShowContractors(!showContractors)}
        >
          {payout.contractors}
        </Toggle>
        <Spacer />
        <AddLink onClick={() => toggleIsAdding(!isAdding)}>
          {payout.add_contractor}
        </AddLink>
      </Controls>

      {isAdding && (
        <AddRow>
          <AddInput
            autoFocus
            value={newName}
            placeholder={payout.contractor_name}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <AddButton disabled={!newName.trim()} onClick={handleAdd}>
            {payout.add}
          </AddButton>
        </AddRow>
      )}

      {grid.showAll ? (
        <Scroller ref={scroller} className="scrollbar">
          <Table>
            <thead>
              <tr>
                <NameHead>{payout.person_column}</NameHead>
                {grid.periods.map((period) => (
                  <MonthHead key={period}>{columnLabel(period)}</MonthHead>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.ref}>
                  {row.ref === firstContractorRef && (
                    <tr>
                      <SectionCell colSpan={grid.periods.length + 1}>
                        {payout.contractors}
                      </SectionCell>
                    </tr>
                  )}
                  <tr>
                  <NameCell onClick={() => openPayee(row)}>
                    <Name isMuted={row.isArchived}>{row.name}</Name>
                  </NameCell>
                  {row.cells.map((cell) => (
                    <Cell key={cell.period} source={cell.source} title={cell.note}>
                      {renderAmount(row, cell)}
                    </Cell>
                  ))}
                  </tr>
                </React.Fragment>
              ))}
              <tr>
                <NameHead as="td"><Total>{payout.total}</Total></NameHead>
                {grid.periods.map((period) => (
                  <TotalCell key={period}>
                    {totalText(grid, period)}
                  </TotalCell>
                ))}
              </tr>
            </tbody>
          </Table>
        </Scroller>
      ) : (
        <Compact className="scrollbar">
          <CompactHead>
            <span />
            {grid.periods.map((period) => (
              <CompactMonth key={period}>{columnLabel(period)}</CompactMonth>
            ))}
          </CompactHead>

          {rows.map((row) => (
            <React.Fragment key={row.ref}>
              {row.ref === firstContractorRef && (
                <SectionLabel>{payout.contractors}</SectionLabel>
              )}
              <CompactRow>
              <CompactName
                isMuted={row.isArchived}
                onClick={() => openPayee(row)}
              >
                {row.name}
              </CompactName>
                {row.cells.map((cell) => (
                  <CompactAmount key={cell.period} source={cell.source} title={cell.note}>
                    {renderAmount(row, cell)}
                  </CompactAmount>
                ))}
              </CompactRow>
            </React.Fragment>
          ))}

          <CompactRow isTotal>
            <CompactName isMuted={false}>{payout.total}</CompactName>
            {grid.periods.map((period) => (
              <CompactAmount key={period} source="none">
                <strong>
                  {totalText(grid, period)}
                </strong>
              </CompactAmount>
            ))}
          </CompactRow>
        </Compact>
      )}

      {openRow && openRow.userId && (
        // Keyed by payee: without it React reuses the instance when a different
        // person is opened, carrying the previous one's expanded sections over.
        <PayeeSheet
          key={openRow.ref}
          userId={openRow.userId}
          name={openRow.name}
          onClose={() => setOpenRow(null)}
          onChanged={() => dispatch(getPayoutGrid(showAll))}
        />
      )}

      {openRow && openRow.contractorId && (
        <ContractorSheet
          key={openRow.ref}
          row={openRow}
          onRename={(name) => {
            dispatch(renameContractor(openRow.contractorId!, name, showAll));
            setOpenRow(null);
          }}
          onArchive={(archived) => {
            dispatch(archiveContractor(openRow.contractorId!, archived, showAll));
            setOpenRow(null);
          }}
          onClose={() => setOpenRow(null)}
        />
      )}

      <Navigation path="payouts" />
    </>
  );
};

const ROW_HEIGHT = '52px';

const SectionLabel = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.2px;
  color: #909599;
  padding: 20px 0 6px;
  border-top: solid 1px #dae1e8;
  margin-top: 8px;
`;

const SectionCell = styled.td`
  position: sticky;
  left: 0;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.2px;
  color: #909599;
  background: #f7f8f9;
  padding: 8px 12px;
  border-top: solid 1px #dae1e8;
  border-bottom: solid 1px #dae1e8;
`;

const Header = styled.header`
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.17);
  background-color: #ffffff;
  padding: 14px 16px;
  position: sticky;
  z-index: 100;
  top: 0;
`;

const HeaderText = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
`;

const HeaderNote = styled.div`
  font-size: 12px;
  color: #909599;
  margin-top: 2px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px 8px;
  background: #ffffff;
`;

const Toggle = styled.div<{ active: boolean }>`
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 6px 10px;
  margin-right: 8px;
  border-radius: 20px;
  cursor: pointer;
  user-select: none;
  color: ${({ active }) => (active ? '#ffffff' : '#787c80')};
  background: ${({ active }) => (active ? '#21272e' : '#f0f1f2')};
`;

const Spacer = styled.div`
  flex: 1;
`;

const AddLink = styled.div`
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #fb7e14;
  cursor: pointer;
  white-space: nowrap;
`;

const AddRow = styled.div`
  display: flex;
  padding: 0 16px 12px;
  background: #ffffff;
`;

const AddInput = styled.input`
  flex: 1;
  height: 40px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 0 12px;
  font-size: 14px;
  outline: none;

  :focus {
    border-color: #fb7e14;
  }
`;

const AddButton = styled.button`
  height: 40px;
  margin-left: 8px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  background: #fb7e14;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  :disabled {
    background: #dae1e8;
    cursor: default;
  }
`;

/* ---- compact layout: the default two-column view ---- */

const Compact = styled.div`
  background: #ffffff;
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 80px;
`;

const CompactHead = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0 8px;
  position: sticky;
  top: 0;
  background: #ffffff;

  > span:first-child {
    flex: 1;
  }
`;

const CompactMonth = styled.div`
  width: 96px;
  text-align: right;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #909599;
`;

const CompactRow = styled.div<{ isTotal?: boolean }>`
  display: flex;
  align-items: center;
  height: ${ROW_HEIGHT};
  border-top: solid 1px ${({ isTotal }) => (isTotal ? '#dae1e8' : '#f4f6f8')};
`;

const CompactName = styled.div<{ isMuted: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 12px;
  color: ${({ isMuted }) => (isMuted ? '#b4babf' : '#21272e')};
`;

const CompactAmount = styled.div<{ source: string }>`
  width: 96px;
  text-align: right;
  font-size: 14px;
  white-space: nowrap;
  color: ${({ source }) => {
    if (source === 'none') return '#d3d8dd';
    if (source === 'projected') return '#a35b00';
    return '#21272e';
  }};
`;

/* ---- full-history layout: sticky name column, horizontal scroll ---- */

const Scroller = styled.div`
  overflow: auto;
  background: #ffffff;
  flex: 1;
  padding-bottom: 72px;
`;

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
`;

const NameHead = styled.th`
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  text-align: left;
  padding: 8px 12px;
  min-width: 150px;
  max-width: 150px;
  height: ${ROW_HEIGHT};
  border-bottom: solid 1px #dae1e8;
  border-right: solid 1px #dae1e8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #909599;
`;

const MonthHead = styled.th`
  padding: 8px 10px;
  white-space: nowrap;
  text-align: right;
  border-bottom: solid 1px #dae1e8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #909599;
`;

const NameCell = styled.td`
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  padding: 8px 12px;
  min-width: 150px;
  max-width: 150px;
  height: ${ROW_HEIGHT};
  border-right: solid 1px #dae1e8;
  border-bottom: solid 1px #f4f6f8;
  cursor: pointer;
`;

const Name = styled.div<{ isMuted: boolean }>`
  font-weight: 600;
  color: ${({ isMuted }) => (isMuted ? '#b4babf' : '#21272e')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Cell = styled.td<{ source: string }>`
  padding: 8px 10px;
  height: ${ROW_HEIGHT};
  text-align: right;
  white-space: nowrap;
  border-bottom: solid 1px #f4f6f8;
  color: ${({ source }) => {
    if (source === 'none') return '#d3d8dd';
    if (source === 'projected') return '#a35b00';
    return '#21272e';
  }};
`;

const Total = styled.span`
  font-weight: 800;
  color: #21272e;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
`;

const TotalCell = styled.td`
  padding: 10px;
  height: ${ROW_HEIGHT};
  text-align: right;
  white-space: nowrap;
  font-weight: 800;
  color: #21272e;
  border-top: solid 1px #dae1e8;
`;

const Uah = styled.div`
  font-size: 11px;
  line-height: 1.2;
`;

export default Payouts;
